// Shop tab in the Conversation right rail: the Shopify customer behind this
// conversation (how they were matched, and whether that is verified), their
// recent orders with fulfilment and tracking, the cart they have open on the
// storefront right now, and lookups for any order or product. Read-only.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { VisitorId } from '@prakash-software/shared';
import { isApiError } from '../lib/api';
import {
  findShopOrder,
  fmtCents,
  fmtMoney,
  getVisitorShop,
  searchShopProducts,
  type ShopOrder,
  type ShopProduct,
  type VisitorShopContext,
} from '../lib/shopify-api';
import styles from './ShopPanel.module.css';

const REFRESH_MS = 30_000;

const MATCH_TEXT: Record<NonNullable<VisitorShopContext['match']>['basis'], string> = {
  'email-channel': 'Matched by the sender of this email conversation (SPF/DKIM verified).',
  'storefront-login': 'Matched by the customer account logged in on the store. This comes from the visitor’s browser: confirm identity before sharing order details.',
  'visitor-email': 'Matched by the email the visitor typed. Not verified: confirm identity before sharing order details.',
};

function errorText(err: unknown): string {
  if (isApiError(err) && err.body && typeof err.body === 'object') {
    const detail = (err.body as { detail?: string }).detail;
    if (detail) return detail;
  }
  return 'Could not reach the Shopify store.';
}

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');
const humanize = (s: string | null) => (s ? s.replace(/_/g, ' ').toLowerCase() : null);

function ago(iso: string | null): string {
  if (!iso) return '';
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return `${Math.round(s / 3600)} h ago`;
}

function OrderCard({ order, shopDomain }: { order: ShopOrder; shopDomain?: string }) {
  const items = order.items.map((i) => `${i.quantity}× ${i.title}`).join(', ');
  return (
    <div className={styles.card}>
      <div className={styles.cardTop}>
        <span>{order.name}</span>
        <span>{fmtMoney(order.total)}</span>
      </div>
      <span className={styles.meta}>
        {fmtDate(order.createdAt)}
        {order.customerName ? ` · ${order.customerName}` : ''}
        {order.customerEmail ? ` · ${order.customerEmail}` : ''}
      </span>
      <div className={styles.pills}>
        {order.cancelled ? <span className={styles.pill}>cancelled</span> : null}
        {order.financialStatus ? <span className={styles.pill}>{humanize(order.financialStatus)}</span> : null}
        {order.fulfillmentStatus ? <span className={styles.pill}>{humanize(order.fulfillmentStatus)}</span> : null}
      </div>
      {items ? <span className={styles.meta}>{items}</span> : null}
      <div className={styles.links}>
        {order.tracking.map((t, i) =>
          t.url ? (
            <a key={i} className={styles.link} href={t.url} target="_blank" rel="noreferrer noopener">
              Track{t.company ? ` (${t.company})` : ''}
            </a>
          ) : t.number ? (
            <span key={i} className={styles.meta}>
              {t.company ?? 'Tracking'}: {t.number}
            </span>
          ) : null,
        )}
        <a className={styles.link} href={order.adminUrl} target="_blank" rel="noreferrer noopener" title={shopDomain}>
          Open in Shopify
        </a>
      </div>
    </div>
  );
}

function ProductCard({ p }: { p: ShopProduct }) {
  const [copied, setCopied] = useState(false);
  const price =
    p.priceMin && p.priceMax && p.priceMin.amount !== p.priceMax.amount
      ? `${fmtMoney(p.priceMin)} – ${fmtMoney(p.priceMax)}`
      : fmtMoney(p.priceMin);
  const inStock = p.variants.filter((v) => v.available).length;
  return (
    <div className={styles.card}>
      <div className={styles.cardTop}>
        <span>{p.title}</span>
        <span>{price}</span>
      </div>
      <span className={styles.meta}>
        {p.totalInventory !== null ? `${p.totalInventory} in stock` : 'Stock not tracked'}
        {p.variants.length > 1 ? ` · ${inStock}/${p.variants.length} variants available` : ''}
        {p.status && p.status !== 'ACTIVE' ? ` · ${humanize(p.status)}` : ''}
      </span>
      <div className={styles.links}>
        {p.url ? (
          <>
            <a className={styles.link} href={p.url} target="_blank" rel="noreferrer noopener">
              View in store
            </a>
            <button
              type="button"
              className={styles.link}
              onClick={() => {
                void navigator.clipboard?.writeText(p.url!).then(() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1500);
                });
              }}
            >
              {copied ? 'Copied ✓' : 'Copy link'}
            </button>
          </>
        ) : (
          <span className={styles.meta}>Not on the online store</span>
        )}
        <a className={styles.link} href={p.adminUrl} target="_blank" rel="noreferrer noopener">
          Open in Shopify
        </a>
      </div>
    </div>
  );
}

export function ShopPanel({ siteId, visitorId }: { siteId: string; visitorId: VisitorId }) {
  const [ctx, setCtx] = useState<VisitorShopContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [orderQ, setOrderQ] = useState('');
  const [order, setOrder] = useState<ShopOrder | null | undefined>(undefined);
  const [productQ, setProductQ] = useState('');
  const [products, setProducts] = useState<ShopProduct[] | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (refresh = false) => {
      try {
        setCtx(await getVisitorShop(siteId, visitorId, refresh));
        setError(null);
      } catch (err) {
        setError(errorText(err));
      } finally {
        setLoading(false);
      }
    },
    [siteId, visitorId],
  );

  useEffect(() => {
    setLoading(true);
    void load();
    // The cart moves while they shop: keep it current while the tab is open.
    const t = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  async function lookupOrder(e: FormEvent) {
    e.preventDefault();
    if (!orderQ.trim()) return;
    setBusy(true); setLookupError(null);
    try {
      setOrder(await findShopOrder(siteId, orderQ.trim()));
    } catch (err) {
      setLookupError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function lookupProducts(e: FormEvent) {
    e.preventDefault();
    if (!productQ.trim()) return;
    setBusy(true); setLookupError(null);
    try {
      setProducts(await searchShopProducts(siteId, productQ.trim()));
    } catch (err) {
      setLookupError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const c = ctx?.customer;
  return (
    <div className={styles.root}>
      <div className={styles.head}>
        <span className={styles.shopName}>Shopify{ctx?.shop.name ? ` · ${ctx.shop.name}` : ''}</span>
        <button type="button" className={styles.link} onClick={() => void load(true)}>
          Refresh
        </button>
      </div>
      {loading && !ctx ? <p className={styles.note}>Loading store data…</p> : null}
      {error ? <p className={styles.error}>{error}</p> : null}

      {ctx ? (
        <section className={styles.section} aria-label="Customer">
          <p className={styles.sectionTitle}>Customer</p>
          {!ctx.match ? (
            <p className={styles.note}>
              No email or store login for this visitor yet, so there is no customer to look up. Ask for their email, or
              find an order by number below.
            </p>
          ) : !c ? (
            <p className={styles.note}>No Shopify customer matches {ctx.match.value}.</p>
          ) : (
            <>
              <p className={ctx.match.verified ? styles.verified : styles.caution}>{MATCH_TEXT[ctx.match.basis]}</p>
              <p className={styles.name}>{c.name ?? c.email ?? 'Customer'}</p>
              <dl className={styles.kv}>
                {c.email ? (<><dt>Email</dt><dd>{c.email}</dd></>) : null}
                {c.phone ? (<><dt>Phone</dt><dd>{c.phone}</dd></>) : null}
                {c.location ? (<><dt>Location</dt><dd>{c.location}</dd></>) : null}
                <dt>Orders</dt>
                <dd>{c.ordersCount}</dd>
                <dt>Spent</dt>
                <dd>{fmtMoney(c.totalSpent)}</dd>
                <dt>Since</dt>
                <dd>{fmtDate(c.createdAt)}</dd>
                {c.tags.length ? (<><dt>Tags</dt><dd>{c.tags.join(', ')}</dd></>) : null}
                {c.note ? (<><dt>Note</dt><dd>{c.note}</dd></>) : null}
              </dl>
              <div className={styles.links}>
                <a className={styles.link} href={c.adminUrl} target="_blank" rel="noreferrer noopener">
                  Open customer in Shopify
                </a>
              </div>
            </>
          )}
        </section>
      ) : null}

      {c && c.orders.length > 0 ? (
        <section className={styles.section} aria-label="Recent orders">
          <p className={styles.sectionTitle}>Recent orders</p>
          {c.orders.map((o) => (
            <OrderCard key={o.id} order={o} shopDomain={ctx?.shop.domain} />
          ))}
        </section>
      ) : null}

      {ctx ? (
        <section className={styles.section} aria-label="Cart">
          <p className={styles.sectionTitle}>Cart on the store</p>
          {!ctx.cart || ctx.cart.itemCount === 0 ? (
            <p className={styles.note}>
              {ctx.cart ? 'Their cart is empty.' : 'No cart seen. It shows up while the visitor browses the store with the chat widget.'}
            </p>
          ) : (
            <>
              {ctx.cart.items.map((i, idx) => (
                <div key={idx} className={styles.cartLine}>
                  {i.image ? <img className={styles.thumb} src={i.image} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <span className={styles.thumb} />}
                  <span>
                    {i.quantity}× {i.title}
                    {i.variantTitle ? <span className={styles.meta}> · {i.variantTitle}</span> : null}
                  </span>
                  <span>{fmtCents(i.price * i.quantity, ctx.cart!.currency)}</span>
                </div>
              ))}
              <div className={styles.total}>
                <span>
                  {ctx.cart.itemCount} item{ctx.cart.itemCount === 1 ? '' : 's'}
                </span>
                <span>{fmtCents(ctx.cart.totalPrice, ctx.cart.currency)}</span>
              </div>
              {ctx.cart.updatedAt ? <p className={styles.note}>Updated {ago(ctx.cart.updatedAt)}, from the visitor’s browser.</p> : null}
            </>
          )}
        </section>
      ) : null}

      {ctx || error ? (
        <section className={styles.section} aria-label="Lookup">
          <p className={styles.sectionTitle}>Find an order</p>
          <form className={styles.search} onSubmit={(e) => void lookupOrder(e)}>
            <input className={styles.input} value={orderQ} onChange={(e) => setOrderQ(e.target.value)} placeholder="#1001" aria-label="Order number" />
            <button type="submit" className={styles.button} disabled={busy || !orderQ.trim()}>Find</button>
          </form>
          {order === null ? <p className={styles.note}>No order with that number.</p> : null}
          {order ? <OrderCard order={order} /> : null}

          <p className={styles.sectionTitle} style={{ marginTop: 'var(--inic-space-2)' }}>Find a product</p>
          <form className={styles.search} onSubmit={(e) => void lookupProducts(e)}>
            <input className={styles.input} value={productQ} onChange={(e) => setProductQ(e.target.value)} placeholder="Name, SKU, tag…" aria-label="Product search" />
            <button type="submit" className={styles.button} disabled={busy || !productQ.trim()}>Search</button>
          </form>
          {lookupError ? <p className={styles.error}>{lookupError}</p> : null}
          {products && products.length === 0 ? <p className={styles.note}>No products match.</p> : null}
          {products?.map((p) => <ProductCard key={p.id} p={p} />)}
        </section>
      ) : null}
    </div>
  );
}
